/// <amd-module name="Test/TestReportingSignalR/TestHub" />
import {
    getTestSignalRHubProxyToRegister,
    HubNames,
    ITestSignalRHubClient,
    ITestSignalRHubServer
} from "Test/TestReportingSignalR/TestHubProxies";
import { IHubHostInformation, IHubListener, SignalRHub } from "VSS/Features/SignalR/Hub";
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IAuthenticationService } from "VSS/Platform/Authentication";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPageService } from "VSS/Platform/Page";

export interface ITestSignalRHubOptions {
    hostInformation: IHubHostInformation;
    projectId: string;
    notificationSync: ITestHubSignalRSync;
    listener?: IHubListener;
    pageContext: IVssPageContext
}

export interface ITestHubSignalRSync {
    testRunStatsChangedForBuild: (buildId: number) => void;
    testRunStatsChangedForRelease: (releaseId: number, releaseEnvironmentId: number) => void;
}

export interface ISignalRUrlData {
    connectionUrl: string;
}

export interface ITestSignalRHubService extends IVssService {
    initialize: (pageContext: IVssPageContext, projectId:string, notificationSync: ITestHubSignalRSync) => Promise<void>;
    watchRelease: (releaseId: number, releaseEnvironmentId: number) => Promise<void>;
    unwatchRelease: (releaseId: number, releaseEnvironmentId: number) => Promise<void>;
    watchBuild: (buildId: number) => Promise<void>;
    unwatchBuild: (buildId: number) => Promise<void>;
    dispose: () => void;
}

class TestSignalRHub extends SignalRHub<ITestSignalRHubClient, ITestSignalRHubServer> {
    private static s_userInactivityTimeoutMs = 1 * 60 * 60 * 1000;
    private _options: ITestSignalRHubOptions;
    private _watchedReleases: Set<string>;
    private _watchedBuilds: Set<number>;
    private _started: boolean;

    constructor (options: ITestSignalRHubOptions) {
        super ({
            hostInformation: options.hostInformation,
            name: HubNames.testHub,
            listener: options.listener,
            userInactivityTimeoutMs: TestSignalRHub.s_userInactivityTimeoutMs,
            pageContext: options.pageContext,
            tokenProvider: options.pageContext.getService<IAuthenticationService>("IAuthenticationService").getNamedTokenProvider("TCM.LiveTestResults")
        });

        this._options = options;
        this._watchedBuilds = new Set();
        this._watchedReleases = new Set();
        this.initializeProxy(getTestSignalRHubProxyToRegister, this._initializeClient);
    }

    public watchRelease(releaseId: number, releaseEnvironmentId: number): Promise<void> {
        const releaseIdentifier = `${releaseId}-${releaseEnvironmentId}`;
        if (this._watchedReleases.has(releaseIdentifier)) {
            Promise.resolve();
        }

        return new Promise<void> ((resolve, reject) => {
            this.start().then(() => {
                const hub = this.hub;
                if (hub) {
                    hub.server.watchRelease(releaseId, releaseEnvironmentId).then(() => {
                        this._watchedReleases.add(releaseIdentifier)
                        this._started = true;
                        resolve();
                    })
                    .catch ((err) => {
                        reject(err);
                    } )
                } else {
                    reject(`testHub: signalR hub is not initialized`);
                }
            }).catch((err) => {
                reject(err);
            });
        });
    }

    public watchBuild(buildId: number): Promise<void> {
        if (this._watchedBuilds.has(buildId)) {
            Promise.resolve();
        }
        return new Promise<void> ((resolve, reject) => {
            this.start().then(() => {
                const hub = this.hub;
                if (hub) {
                    hub.server.watchBuild(buildId).then(() => {
                        this._watchedBuilds.add(buildId);
                        this._started = true;
                        resolve();
                    })
                    .catch ((err) => {
                        reject(err);
                    });
                } else {
                    reject(`testHub: signalR hub is not initialized`);
                }
            }).catch((err) => {
                reject(err);
            });
        });
    }

    public unwatchRelease(releaseId: number, releaseEnvironmentId: number): Promise<void> {
        const hub = this.hub;
        const releaseIdentifier = `${releaseId}-${releaseEnvironmentId}`;
        return new Promise<void>((resolve, reject) => {
            if (hub && this._watchedReleases.has(releaseIdentifier) && this._started) {
                hub.server.unwatchRelease(releaseId, releaseEnvironmentId).then(() => {
                    this._watchedReleases.delete(releaseIdentifier);
                    resolve();
                }).catch((reason: any) => {
                    reject(reason);
                });
            } else {
                return Promise.resolve();
            }

        });
    }

    public unwatchBuild(buildId: number): Promise<void> {
        const hub = this.hub;
        const watchedBuilds = this._watchedBuilds;
        return new Promise<void>( (resolve, reject) => {
            if (hub && watchedBuilds.has(buildId) && this._started) {
                hub.server.unwatchBuild(buildId).then(() => {
                     watchedBuilds.delete(buildId);
                     resolve();
                 }).catch((reason:any) => {
                     reject(reason);
                 });
            } else {
                return Promise.resolve();
            }
        });
    }

    public dispose() {
        if (this._started) {
            super.dispose();
        }
    }

    private _initializeClient = (proxy: ISignalRHubProxy<ITestSignalRHubClient, ITestSignalRHubServer>) => {
        proxy.client.testRunStatsChangedForBuild = (buildId: number) => {
            if(this._options && this._options.notificationSync) {
                this._options.notificationSync.testRunStatsChangedForBuild(buildId);
            }
        };

        proxy.client.testRunStatsChangedForRelease = (releaseId: number, releaseEnvironmentId: number) => {
            if(this._options && this._options.notificationSync) {
                this._options.notificationSync.testRunStatsChangedForRelease(releaseId, releaseEnvironmentId);
            }
        };
    }
}

class TestSignalRHubService extends VssService implements ITestSignalRHubService, ITestHubSignalRSync {
    private static s_testSignalRHub: TestSignalRHub | undefined;
    private static s_notificationCallBacks: ITestHubSignalRSync[];
    private static s_notficationCallBacksLength: number;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext)
        TestSignalRHubService.s_notificationCallBacks = [];
        TestSignalRHubService.s_notficationCallBacksLength = 0;
        TestSignalRHubService.s_testSignalRHub = undefined;
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
        if (TestSignalRHubService.s_testSignalRHub) {
            TestSignalRHubService.s_testSignalRHub.dispose();
        }
        TestSignalRHubService.s_notificationCallBacks = []
        TestSignalRHubService.s_notficationCallBacksLength = 0;
    }

    public initialize(pageContext: IVssPageContext, projectId: string, notificationSync: ITestHubSignalRSync): Promise<void> {
        return new Promise( async (resolve, reject) => {
            const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
            const vssPageService = pageContext.getService<IVssPageService>("IVssPageService");
            const vssPageData = vssPageService.getData();
            const signalRData:ISignalRUrlData | undefined = await contributionService.getDataAsync<ISignalRUrlData>("ms.vss-test-web.test-tab-signalr-data-provider", {}, false);
    
            if(!vssPageData || !signalRData || !notificationSync || !projectId) {
                reject("Invalid parameters.");
                return;
            }
    
            const hostId = vssPageData.hostId;
            const isHosted = vssPageData.isHosted;
            const hostInformation: IHubHostInformation = {
                hostId: hostId,
                isHosted: isHosted,
                hostPath: signalRData.connectionUrl.split("_apis")[0],
                projectId: projectId
            };

            if(!TestSignalRHubService.s_testSignalRHub) {
                const signalrHubOptions: ITestSignalRHubOptions = {
                    hostInformation: hostInformation,
                    projectId: projectId,
                    notificationSync: this,
                    pageContext: pageContext
                };
                TestSignalRHubService.s_testSignalRHub = new TestSignalRHub(signalrHubOptions);
            }
            TestSignalRHubService.s_notificationCallBacks.push(notificationSync);
            TestSignalRHubService.s_notficationCallBacksLength += 1;
            resolve();
        });
    }

    public watchRelease(releaseId: number, releaseEnvironmentId: number): Promise<void> {
        if (TestSignalRHubService.s_testSignalRHub) {
            return TestSignalRHubService.s_testSignalRHub.watchRelease(releaseId, releaseEnvironmentId);
        } else {
            return Promise.reject(`testHub: TestSignalRHub is not initialized`)
        }
    }

    public watchBuild(buildId: number): Promise<void> {
        if (TestSignalRHubService.s_testSignalRHub) {
            return TestSignalRHubService.s_testSignalRHub.watchBuild(buildId);
        } else {
            return Promise.reject(`testHub: TestSignalRHub is not initialized`)
        }
    }

    public unwatchRelease(releaseId: number, releaseEnvironmentId: number): Promise<void> {
        if (TestSignalRHubService.s_testSignalRHub && TestSignalRHubService.s_notficationCallBacksLength == 1) {
            return TestSignalRHubService.s_testSignalRHub.unwatchRelease(releaseId, releaseEnvironmentId);
        } else {
            return Promise.resolve();
        }
    }

    public unwatchBuild(buildId: number): Promise<void> {
        if (TestSignalRHubService.s_testSignalRHub && TestSignalRHubService.s_notficationCallBacksLength == 1) {
            return TestSignalRHubService.s_testSignalRHub.unwatchBuild(buildId);
        } else {
            return Promise.resolve();
        }
    }

    public dispose() {
        if (!TestSignalRHubService.s_testSignalRHub) {
            return;
        }

        TestSignalRHubService.s_notficationCallBacksLength -= 1;
        if ( TestSignalRHubService.s_notficationCallBacksLength <= 0 ) {
            TestSignalRHubService.s_testSignalRHub.dispose();
            TestSignalRHubService.s_testSignalRHub = undefined;
            console.log("testHub: Disconnected")
        }
    }

    public testRunStatsChangedForBuild(buildId: number): void {
        TestSignalRHubService.s_notificationCallBacks.forEach(element => {
            try {
                element.testRunStatsChangedForBuild(buildId);
            } catch(error) {
                console.warn("testHub: Error in calling the callback");
            }
        });
    }

    public testRunStatsChangedForRelease(releaseId: number, releaseEnvironmentId: number): void {
        TestSignalRHubService.s_notificationCallBacks.forEach(element => {
            try {
                element.testRunStatsChangedForRelease(releaseId, releaseEnvironmentId);
            } catch (error) {
                console.warn("testHub: Error in calling the callback");
            }
        });
    }
}

Services.add("ITestSignalRHub", {serviceFactory: TestSignalRHubService});